/**
 * Service Worker Registration
 *
 * Service Worker를 등록하고 업데이트를 관리
 */

export interface SWRegistrationConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

let registration: ServiceWorkerRegistration | null = null;

/**
 * Service Worker 등록
 */
export async function registerServiceWorker(
  config: SWRegistrationConfig = {}
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  // 개발 환경에서는 비활성화 옵션
  if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_SW_DEV) {
    // 과거 세션에서 등록된 SW가 남아 있으면 구버전 자산을 계속 서빙해
    // "코드를 고쳐도 새로고침해도 반영 안 되는" 문제를 일으킨다.
    // 개발 모드에서는 기존 SW와 캐시를 적극적으로 제거한다.
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
      if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }
    } catch {
      // SW 해제 실패는 개발 환경에서 치명적이지 않으므로 무시
    }
    return null;
  }

  try {
    registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    // 설치 완료 시
    if (registration.installing) {
      // Service worker installing
    } else if (registration.waiting) {
      config.onUpdate?.(registration);
    } else if (registration.active) {
      config.onSuccess?.(registration);
    }

    // 업데이트 감지
    registration.addEventListener('updatefound', () => {
      const newWorker = registration?.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // 새 버전이 설치됨 - 업데이트 알림
          config.onUpdate?.(registration!);
        }
      });
    });

    // controllerchange 이벤트 (SW가 활성화될 때)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // 필요시 페이지 새로고침
    });

    // SW로부터 메시지 수신
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SYNC_WORKOUTS') {
        // 동기화 요청 처리
        import('./syncService').then(({ syncAllPendingWorkouts }) => {
          syncAllPendingWorkouts();
        });
      }
    });

    return registration;
  } catch (error) {
    console.error('[SW] Registration failed:', error);
    config.onError?.(error as Error);
    return null;
  }
}

/**
 * Service Worker 업데이트 확인
 */
export async function checkForUpdates(): Promise<boolean> {
  if (!registration) return false;

  try {
    await registration.update();
    return registration.waiting !== null;
  } catch (error) {
    console.error('[SW] Update check failed:', error);
    return false;
  }
}

/**
 * 대기 중인 Service Worker 활성화 (skipWaiting)
 */
export function activateWaitingWorker(): void {
  if (!registration?.waiting) return;

  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Service Worker 해제
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!registration) return false;

  try {
    const success = await registration.unregister();
    if (success) {
      registration = null;
    }
    return success;
  } catch (error) {
    console.error('[SW] Unregister failed:', error);
    return false;
  }
}

/**
 * 캐시 삭제
 */
export async function clearCaches(): Promise<void> {
  const cacheNames = await caches.keys();
  await Promise.all(
    cacheNames.map((cacheName) => caches.delete(cacheName))
  );
}

/**
 * 현재 Service Worker 상태 조회
 */
export function getServiceWorkerStatus(): {
  isSupported: boolean;
  isRegistered: boolean;
  isActive: boolean;
  hasUpdate: boolean;
} {
  return {
    isSupported: 'serviceWorker' in navigator,
    isRegistered: registration !== null,
    isActive: registration?.active !== null,
    hasUpdate: registration?.waiting !== null,
  };
}

/**
 * URL 목록을 캐시에 추가
 */
export function cacheUrls(urls: string[]): void {
  if (!registration?.active) return;

  registration.active.postMessage({
    type: 'CACHE_URLS',
    urls,
  });
}

export default {
  registerServiceWorker,
  checkForUpdates,
  activateWaitingWorker,
  unregisterServiceWorker,
  clearCaches,
  getServiceWorkerStatus,
  cacheUrls,
};
